import {ConfigurationPromo} from '../types/configurationPromo'

type GenericRecord = Record<string, unknown>
type LocalizedValue = Record<string, string | null | undefined>

const DEFAULT_TEXT = {
    ctaOpening: 'Richiedi il coupon',
    ctaRecovery: 'Oppure recupera il coupon gia scaricato',
    formTitle: "Inserisci i tuoi dati per richiedere l'offerta",
    fieldNotes: "I campi contrassegnati con l'asterisco sono obbligatori.",
    submit: 'Autorizzo e invio',
    additionalInfo: 'Informazioni aggiuntive',
    privacyPolicyUrl:
        'https://www.volkswagen.it/it/area-clienti/promozioni-e-vantaggi/privacy-promozioni.html',
    thankYouTitle: 'Grazie!',
    thankYouText:
        "Grazie per aver richiesto l'offerta relativa alla promozione :promotion.",
    resendingText:
        "A breve riceverai una email all'indirizzo da te indicato per scaricarla.",
    resendingConfirmation: 'Email reinviata, controlla la tua casella email.',
    footerText:
        'Non perdere le altre promozioni Volkswagen Service dedicate a te e alla tua Volkswagen.',
    footerCtaText: 'Scopri tutte le promozioni',
    footerCtaHref:
        'https://www.volkswagen.it/it/area-clienti/promozioni-e-vantaggi.html',
    thankYouEmailTitle: 'Ecco il tuo coupon',
    thankYouEmailSubject: 'Promozione Volkswagen',
    thankYouEmailText1: 'Gentile <strong>:name :surname,</strong>',
    thankYouEmailText2:
        "grazie per aver richiesto l'offerta relativa alla promozione :promotion.",
    thankYouEmailDownload: "Richiedi l'offerta",
    recoverTitle: "Recupera l'offerta",
    recoverText:
        "Inserisci la targa della Volkswagen per la quale hai precedentemente richiesto l'offerta. Ti sara spedita via email.",
    recoverCta: "Inviami l'offerta",
    recoverThankYouTitle: 'Grazie!',
    recoverThankYouText:
        "Abbiamo inviato l'offerta all'indirizzo precedentemente indicato.",
    teaserTitle: '<div>Promozione Service</div>',
    teaserDescription:
        '<div>Scegliere la qualita ha sempre i suoi vantaggi.</div>',
    teaserCta: 'Scopri la promozione',
    couponText:
        'La presente offerta e riservata in esclusiva alla Volkswagen targata :plate.',
}

const isRecord = (value: unknown): value is GenericRecord =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const toRecord = (value: unknown): GenericRecord =>
    isRecord(value) ? value : {}

const toStringValue = (value: unknown, fallback = ''): string => {
    if (typeof value !== 'string') {
        return fallback
    }

    const cleanedValue = value.trim()

    return cleanedValue.length > 0 ? cleanedValue : fallback
}

const toBooleanValue = (value: unknown, fallback = false): boolean =>
    typeof value === 'boolean' ? value : fallback

const toStringArray = (value: unknown): string[] =>
    Array.isArray(value)
        ? value.filter((entry): entry is string => typeof entry === 'string')
        : []

const toLocalizedValue = (
    value: unknown,
    fallbackNational: string,
    fallbackDwSw?: string
): LocalizedValue => {
    const source = toRecord(value)
    const national = toStringValue(source.national, fallbackNational)
    const dwSw = toStringValue(source.dw_sw, fallbackDwSw || national)

    return {
        ...source,
        national,
        dw_sw: dwSw,
    } as LocalizedValue
}

const toHrefValue = (
    value: unknown
): Record<string, string | null | undefined> => {
    const source = toRecord(value)
    const national = toStringValue(source.national, DEFAULT_TEXT.footerCtaHref)
    const dw = toStringValue(source.dw, national)
    const sw = toStringValue(source.sw, national)

    return {
        ...source,
        national,
        dw,
        sw,
    }
}

export const normalizeConfigurationPromo = (
    value: unknown
): ConfigurationPromo => {
    const configuration = toRecord(value)
    const ctaOpening = toRecord(configuration.cta_opening)
    const dealersSearchParams = toRecord(configuration.dealers_search_params)
    const form = toRecord(configuration.form)
    const plateField = toRecord(form.plate_field)
    const modelsField = toRecord(form.models_field)
    const additionalInfoField = toRecord(form.additional_info_field)
    const thankYouPage = toRecord(configuration.thank_you_page)
    const thankYouResending = toRecord(thankYouPage.resending)
    const thankYouFooter = toRecord(thankYouPage.footer)
    const thankYouFooterCta = toRecord(thankYouFooter.cta)
    const thankYouEmail = toRecord(configuration.thank_you_email)
    const coupon = toRecord(configuration.coupon)
    const couponRecovery = toRecord(coupon.recovery)
    const couponRecoveryThanks = toRecord(couponRecovery.thank_you_page)
    const teaser = toRecord(configuration.teaser)
    const leadInbox = toRecord(configuration.lead_inbox)

    return {
        ...configuration,
        brand: toStringValue(configuration.brand, 'VW'),
        allowed_parameters: toStringArray(configuration.allowed_parameters),
        begin_date: toStringValue(configuration.begin_date),
        end_date: toStringValue(configuration.end_date),
        enabled: toBooleanValue(configuration.enabled, false),
        name: toStringValue(configuration.name),
        code: toStringValue(configuration.code),
        cta_opening: {
            ...ctaOpening,
            national: toStringValue(
                ctaOpening.national,
                DEFAULT_TEXT.ctaOpening
            ),
            dw_sw: toStringValue(ctaOpening.dw_sw, DEFAULT_TEXT.ctaOpening),
            hide_cta: toBooleanValue(ctaOpening.hide_cta, false),
            coupon_recovery: toStringValue(
                ctaOpening.coupon_recovery,
                DEFAULT_TEXT.ctaRecovery
            ),
        },
        dealers_search_params: {
            ...dealersSearchParams,
            brand: toStringValue(dealersSearchParams.brand, 'VW'),
            activity_type: toStringValue(dealersSearchParams.activity_type),
            contract_type: toStringValue(dealersSearchParams.contract_type),
            blacklisted_codes: toStringArray(
                dealersSearchParams.blacklisted_codes
            ),
        },
        form: {
            ...form,
            title: toLocalizedValue(form.title, DEFAULT_TEXT.formTitle),
            description: toLocalizedValue(form.description, ''),
            field_notes: toStringValue(
                form.field_notes,
                DEFAULT_TEXT.fieldNotes
            ),
            additional_info_field: {
                ...additionalInfoField,
                enabled: toBooleanValue(additionalInfoField.enabled, false),
                label: toStringValue(
                    additionalInfoField.label,
                    DEFAULT_TEXT.additionalInfo
                ),
            },
            plate_field: {
                ...plateField,
                enabled: toBooleanValue(plateField.enabled, true),
                unique: toBooleanValue(plateField.unique, false),
                vgi_validation: toBooleanValue(
                    plateField.vgi_validation,
                    false
                ),
                unique_for_dealer: toBooleanValue(
                    plateField.unique_for_dealer,
                    false
                ),
            },
            models_field: {
                ...modelsField,
                enabled: toBooleanValue(modelsField.enabled, false),
                enabled_models: toRecord(modelsField.enabled_models),
            },
            cta_submit: toLocalizedValue(form.cta_submit, DEFAULT_TEXT.submit),
            privacy_policy_url: toStringValue(
                form.privacy_policy_url,
                DEFAULT_TEXT.privacyPolicyUrl
            ),
        },
        thank_you_page: {
            ...thankYouPage,
            title: toLocalizedValue(
                thankYouPage.title,
                DEFAULT_TEXT.thankYouTitle
            ),
            text: toLocalizedValue(
                thankYouPage.text,
                DEFAULT_TEXT.thankYouText
            ),
            resending: {
                ...thankYouResending,
                enabled: toBooleanValue(thankYouResending.enabled, false),
                text: toLocalizedValue(
                    thankYouResending.text,
                    DEFAULT_TEXT.resendingText
                ),
                confirmation_text: toLocalizedValue(
                    thankYouResending.confirmation_text,
                    DEFAULT_TEXT.resendingConfirmation
                ),
            },
            footer: {
                ...thankYouFooter,
                enabled: toBooleanValue(thankYouFooter.enabled, false),
                text: toLocalizedValue(
                    thankYouFooter.text,
                    DEFAULT_TEXT.footerText
                ),
                cta: {
                    ...thankYouFooterCta,
                    text: toLocalizedValue(
                        thankYouFooterCta.text,
                        DEFAULT_TEXT.footerCtaText
                    ),
                    href: toHrefValue(thankYouFooterCta.href),
                },
            },
        },
        thank_you_email: {
            ...thankYouEmail,
            enabled: toBooleanValue(thankYouEmail.enabled, false),
            title: toLocalizedValue(
                thankYouEmail.title,
                DEFAULT_TEXT.thankYouEmailTitle
            ),
            subject: toLocalizedValue(
                thankYouEmail.subject,
                DEFAULT_TEXT.thankYouEmailSubject
            ),
            header_img: toLocalizedValue(thankYouEmail.header_img, ''),
            text1: toLocalizedValue(
                thankYouEmail.text1,
                DEFAULT_TEXT.thankYouEmailText1
            ),
            text2: toLocalizedValue(
                thankYouEmail.text2,
                DEFAULT_TEXT.thankYouEmailText2
            ),
            note: toLocalizedValue(thankYouEmail.note, ''),
            coupon_download_cta: toLocalizedValue(
                thankYouEmail.coupon_download_cta,
                DEFAULT_TEXT.thankYouEmailDownload
            ),
        },
        coupon: {
            ...coupon,
            coupon_generation: toBooleanValue(coupon.coupon_generation, false),
            recoverable: toBooleanValue(coupon.recoverable, false),
            img: toStringValue(coupon.img),
            text: toLocalizedValue(coupon.text, DEFAULT_TEXT.couponText),
            legal_notes: toLocalizedValue(coupon.legal_notes, ''),
            expiration_date: toStringValue(coupon.expiration_date),
            recovery: {
                ...couponRecovery,
                title: toStringValue(
                    couponRecovery.title,
                    DEFAULT_TEXT.recoverTitle
                ),
                text: toStringValue(
                    couponRecovery.text,
                    DEFAULT_TEXT.recoverText
                ),
                cta: toStringValue(couponRecovery.cta, DEFAULT_TEXT.recoverCta),
                thank_you_page: {
                    ...couponRecoveryThanks,
                    title: toStringValue(
                        couponRecoveryThanks.title,
                        DEFAULT_TEXT.recoverThankYouTitle
                    ),
                    text: toStringValue(
                        couponRecoveryThanks.text,
                        DEFAULT_TEXT.recoverThankYouText
                    ),
                },
            },
        },
        teaser: {
            ...teaser,
            cta_label: toStringValue(teaser.cta_label, DEFAULT_TEXT.teaserCta),
            cta_url: toStringValue(teaser.cta_url),
            description: toStringValue(
                teaser.description,
                DEFAULT_TEXT.teaserDescription
            ),
            img: toStringValue(teaser.img),
            title: toStringValue(teaser.title, DEFAULT_TEXT.teaserTitle),
        },
        tracking_application_id: toStringValue(
            configuration.tracking_application_id
        ),
        lead_inbox: {
            ...leadInbox,
            campaign_code: toLocalizedValue(leadInbox.campaign_code, ''),
            lead_title: toStringValue(leadInbox.lead_title),
            privacy_origin: toStringValue(leadInbox.privacy_origin),
            interest_service: toStringValue(leadInbox.interest_service),
            invalid_campaigns_alert_email_recipients: toStringArray(
                leadInbox.invalid_campaigns_alert_email_recipients
            ),
        },
    } as ConfigurationPromo
}

export const normalizePromotionList = (value: unknown): ConfigurationPromo[] =>
    Array.isArray(value)
        ? value.map((entry: unknown) => normalizeConfigurationPromo(entry))
        : []
